import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { useMutation } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

import { BackButton } from '@components/atoms/BackButton'
import { CustomRewardsForm } from '@components/organisms/CustomRewardsForm'
import { Popover } from '@/components/molecules/Popover'

import { createCustomRewards, IRewardDTO } from '@services/rewards'
import { errorBoundary } from '@/services/errorBoundary'
import { useWhiteList } from '@/lib/hooks/useWhiteList'

import { NotFound } from '../NotFound'

export const CustomRewards: FC = () => {
  const { t } = useTranslation('common')
  const { isWhitelistedUser } = useWhiteList()

  const [customRewardsData, setCustomRewardsData] = useState<IRewardDTO | null>(
    null
  )
  const [submitError, setSubmitError] = useState<AxiosResponse<never> | string>(
    ''
  )

  const { mutate: createCustomRewardsMutation, isPending: mutationLoading } =
    useMutation({
      mutationKey: ['createCustomRewards'],
      mutationFn: createCustomRewards,
      onError: (error: AxiosError) => {
        errorBoundary(error)
        setSubmitError(error.response.data)
      },
      onSuccess: (data) => {
        setCustomRewardsData(data)
      },
    })

  const onSubmit = async (data) => {
    createCustomRewardsMutation({
      usernames: data.usernames
        .split(/\r?\n/)
        .filter((username: string) => username),
      contestId: data.contestId,
      postMessage: data.postMessage,
      activityMessage: data.activityMessage,
      reward: Number(data.reward),
      publishPost: data.publishPost,
    } as IRewardDTO)
  }

  if (!isWhitelistedUser) return <NotFound />

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <BackButton defaultHref="/feed" />
          </IonButtons>
          <IonTitle class="text-center">{t('customRewards.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton>
              <IonIcon slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <div className="max-w-md mx-auto px-6 pt-8">
          <CustomRewardsForm onSubmit={onSubmit} isLoading={mutationLoading} />
          <Popover
            isOpen={Boolean(customRewardsData)}
            type="success"
            onDismiss={() => setCustomRewardsData(null)}
          >
            {t('customRewards.alertMessage')}
          </Popover>
          <Popover
            isOpen={Boolean(submitError)}
            type="danger"
            onDismiss={() => setSubmitError('')}
          >
            <p className="text-xs">{submitError}</p>
          </Popover>
        </div>
      </IonContent>
    </IonPage>
  )
}
